/**
 * HealthReportView — the Portfolio Health Report as a DOCUMENT (Alethos redesign,
 * "with tabs"). A full-width reading view: a sticky sub-header with section jump
 * links, a title block, a "short version" callout, a key-figures table (derived
 * deterministically from the X-ray), the Fable prose sections with a stress-test
 * callout, and a close bar. "Download PDF" prints (print CSS isolates the doc).
 * Descriptive — not investment advice.
 */
import { useEffect } from "react";
import { fmtINR } from "@/lib/format";
import { useIsMobile, responsiveCols } from "../hooks/useBreakpoint";
import type { PortfolioHealthReport, XRayResponse } from "@/lib/api";

const MONO: React.CSSProperties = { fontFamily: "var(--a-mono)" };
const DISPLAY = "var(--a-display)";
const WARN = "var(--warning-fg, #C2700A)";
const GOOD = "var(--a-success-fg)";
const BAD = "var(--a-danger-fg)";

const pctStr = (n: number | null | undefined, dp = 1, sign = false) =>
  n == null ? "—" : `${sign && n >= 0 ? "+" : ""}${n.toFixed(dp)}%`;
const lakh = (n: number) => `₹${(n / 100000).toFixed(2)} L`;
const secId = (i: number) => `hrv-sec-${i}`;
const shortLabel = (title: string) => title.split(/[\s&/]+/)[0];

interface Fig { m: string; v: string; read: string; tone?: "warn" | "good" | "bad" }

function buildFigures(x: XRayResponse): { figures: Fig[]; stress: { p: number; loss: number }[]; book: number } {
  const book = (x.equity_value || 0) + (x.mf_value || 0);
  const topH = x.concentration?.top_holdings?.[0];
  const topSec = x.allocation?.by_sector?.[0];
  const r: Partial<NonNullable<XRayResponse["risk"]>> = x.risk ?? {};
  const q: Partial<NonNullable<XRayResponse["quality_tilt"]>> = x.quality_tilt ?? {};
  const scBreach = topH && topH.pct > 15;
  const secBreach = topSec && topSec.pct > 30;

  const figures: Fig[] = [];
  if (book) figures.push({ m: "Book value", v: fmtINR(book, { abs: true }), read: `${x.concentration?.top_holdings?.length ?? "—"} priced positions` });
  if (topH) figures.push({ m: "Largest position", v: `${topH.pct}%`, read: `${topH.ticker} · your limit 15%`, tone: scBreach ? "warn" : undefined });
  if (topSec) figures.push({ m: "Largest sector", v: `${topSec.pct}%`, read: `${topSec.name} · your limit 30%`, tone: secBreach ? "warn" : undefined });
  if (r.beta_vs_nifty50 != null) figures.push({ m: "Beta vs Nifty 50", v: r.beta_vs_nifty50.toFixed(2), read: r.beta_vs_nifty50 >= 1 ? "moves with the market" : "steadier than the index" });
  if (r.volatility_ann_pct != null) figures.push({ m: "Volatility (annualised)", v: pctStr(r.volatility_ann_pct), read: "spread of returns over the year" });
  if (r.max_drawdown_pct != null) figures.push({ m: "Max drawdown", v: pctStr(r.max_drawdown_pct), read: "worst peak-to-trough in the window", tone: "bad" });
  if (r.var_1d_95_pct != null) figures.push({ m: "Value at risk (1d, 95%)", v: pctStr(r.var_1d_95_pct), read: "a normal bad day, 19 days in 20" });
  if (r.excess_return_pct != null) figures.push({ m: "Return vs Nifty 50", v: pctStr(r.excess_return_pct, 2, true), read: `you ${pctStr(r.window_return_pct, 2, true)} vs ${pctStr(r.nifty50_return_pct, 2, true)}`, tone: r.excess_return_pct >= 0 ? "good" : "bad" });
  if (q.weighted_pe?.value != null) figures.push({ m: "Weighted P/E", v: q.weighted_pe.value.toFixed(2), read: `${q.weighted_pe.coverage_pct}% of book covered` });
  if (q.weighted_roe_pct?.value != null) figures.push({ m: "Weighted ROE", v: pctStr(q.weighted_roe_pct.value), read: "quality tilt of the book" });

  const beta = Math.abs(r.beta_vs_nifty50 ?? 1);
  const stress = [10, 20, 30].map((p) => ({ p, loss: book * beta * (p / 100) }));
  return { figures, stress, book };
}

const toneColor = (t?: "warn" | "good" | "bad") => t === "warn" ? WARN : t === "good" ? GOOD : t === "bad" ? BAD : "var(--a-text-strong)";

export function HealthReportView({ report, xray, generatedOn, preparedFor, autoPrint, onClose, onAsk }: {
  report: PortfolioHealthReport; xray: XRayResponse | null; generatedOn: string;
  preparedFor?: string; autoPrint?: boolean; onClose: () => void; onAsk?: () => void;
}) {
  const { figures, stress, book } = xray ? buildFigures(xray) : { figures: [], stress: [], book: 0 };
  const isMobile = useIsMobile();
  // Measure / Value / Read collapses to Measure + Value, with the read wrapping
  // onto its own full-width line below — three columns in ~290px would leave the
  // read a two-word-per-line ribbon.
  const figCols = responsiveCols(isMobile, "minmax(0,1.1fr) minmax(0,130px) minmax(0,1.2fr)", "minmax(0,1fr) auto");
  useEffect(() => {
    if (!autoPrint) return;
    const t = setTimeout(() => window.print(), 300);
    return () => clearTimeout(t);
  }, [autoPrint]);

  const jump = (i: number) => document.getElementById(secId(i))?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div id="hrv-overlay" className="alethos" onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 120, background: "rgba(12,17,16,0.45)", overflowY: "auto", padding: isMobile ? "10px 8px" : "24px 20px" }}>
      <div className="hrv-doc" onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 1120, margin: "0 auto", background: "var(--ink-0)", borderRadius: 16, boxShadow: "0 24px 60px -24px rgba(12,17,16,0.5)", overflow: "hidden" }}>
        {/* Sticky sub-header with jump links (the "tabs") */}
        <div className="hrv-chrome" style={{ position: "sticky", top: 0, zIndex: 3, background: "rgba(255,255,255,0.92)", backdropFilter: "blur(12px)", borderBottom: "1px solid var(--a-border-subtle)", padding: isMobile ? "9px 14px" : "11px 24px", display: "flex", alignItems: "center", gap: isMobile ? 10 : 16, flexWrap: "wrap" }}>
          <button className="a-btn" style={{ background: "none", border: "none", cursor: "pointer", color: "var(--a-text-muted)", fontSize: 16, padding: 0 }} onClick={onClose} aria-label="Back">←</button>
          <span style={{ fontFamily: DISPLAY, fontSize: 15, fontWeight: 600 }}>Portfolio health report</span>
          <span style={{ ...MONO, fontSize: 9.5, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--a-text-subtle)" }}>{generatedOn} · EOD</span>
          {/* On a phone the jump links stay on ONE swipeable row: wrapped, they build a
              four-line sticky header that covers most of the screen while reading. */}
          <nav style={{ display: "flex", gap: 14, flex: 1, flexWrap: isMobile ? "nowrap" : "wrap", overflowX: isMobile ? "auto" : undefined, minWidth: 0 }}>
            {report.sections.map((s, i) => (
              <button key={i} onClick={() => jump(i)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12.5, color: "var(--a-text-muted)", padding: "2px 0", fontFamily: "var(--a-body)", flex: isMobile ? "0 0 auto" : undefined }}
                onMouseEnter={(e) => { e.currentTarget.style.color = "var(--a-text-strong)"; e.currentTarget.style.borderBottom = "1px solid var(--veris-600)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = "var(--a-text-muted)"; e.currentTarget.style.borderBottom = "none"; }}>{shortLabel(s.title)}</button>
            ))}
          </nav>
          <button className="a-btn a-btn-secondary" style={{ padding: "7px 12px", fontSize: 12.5 }} onClick={() => window.print()}>Download PDF</button>
        </div>

        <div style={{ padding: isMobile ? "20px 16px 30px" : "28px 32px 40px", maxWidth: 760, margin: "0 auto" }}>
          {/* Title block */}
          <div style={{ ...MONO, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.12em", color: "var(--a-text-muted)" }}>Prepared for {preparedFor || "your portfolio"}</div>
          <h1 style={{ fontFamily: DISPLAY, fontSize: isMobile ? 27 : 38, fontWeight: 600, letterSpacing: "-0.032em", lineHeight: 1.12, margin: "6px 0 0", color: "var(--a-text-strong)" }}>Portfolio health report</h1>
          <div style={{ ...MONO, fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--a-text-subtle)", display: "flex", flexWrap: "wrap", gap: "6px 16px", marginTop: 12 }}>
            <span>Generated {generatedOn}</span>
            {book > 0 && <span>Book {fmtINR(book, { abs: true })}</span>}
            {xray?.as_of && <span>Prices {xray.as_of}</span>}
            <span>EOD close</span>
          </div>
          <p style={{ fontSize: 15.5, lineHeight: 1.6, color: "var(--a-text-muted)", margin: "14px 0 0", maxWidth: "34em" }}>
            One read across your whole book — allocation, concentration, risk, performance and fund health — connected rather than reported line by line.
          </p>

          {/* Short version */}
          {report.headline && (
            <div className="hrv-kept" style={{ background: "var(--surface-sunken, var(--ink-50))", border: "1px solid var(--a-border-subtle)", borderRadius: 16, padding: "20px 22px", marginTop: 24 }}>
              <div style={{ ...MONO, fontSize: 9.5, textTransform: "uppercase", letterSpacing: "0.12em", color: "var(--a-text-muted)", marginBottom: 8 }}>The short version</div>
              <p style={{ fontFamily: DISPLAY, fontSize: 20, fontWeight: 500, lineHeight: 1.42, margin: 0, color: "var(--a-text-strong)" }}>{report.headline}</p>
            </div>
          )}

          {/* Key figures table */}
          {figures.length > 0 && (
            <div className="hrv-kept" style={{ marginTop: 28, border: "1px solid var(--a-border-subtle)", borderRadius: 16, overflow: "hidden" }}>
              <div style={{ display: "grid", gridTemplateColumns: figCols, background: "var(--surface-sunken, var(--ink-50))", ...MONO, fontSize: 9, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--a-text-muted)", padding: isMobile ? "9px 14px" : "9px 18px" }}>
                {/* No third column on a phone, so its heading would label nothing. */}
                <span>Measure</span><span style={{ textAlign: "right" }}>Value</span><span style={{ paddingLeft: 14, display: isMobile ? "none" : undefined }}>Read</span>
              </div>
              {figures.map((f, i) => (
                <div key={f.m} style={{ display: "grid", gridTemplateColumns: figCols, alignItems: "center", padding: isMobile ? "10px 14px" : "10px 18px", borderTop: i ? "1px solid var(--a-border-subtle)" : "none" }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "var(--ink-50)"; }} onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
                  <span style={{ fontSize: 14, color: "var(--a-text-body)" }}>{f.m}</span>
                  <span style={{ ...MONO, fontSize: 13.5, textAlign: "right", fontWeight: 600, color: toneColor(f.tone), fontVariantNumeric: "tabular-nums" }}>{f.v}</span>
                  <span style={{ fontSize: 12.5, color: "var(--a-text-subtle)", paddingLeft: isMobile ? 0 : 14, gridColumn: isMobile ? "1 / -1" : undefined, marginTop: isMobile ? 3 : undefined }}>{f.read}</span>
                </div>
              ))}
            </div>
          )}

          {/* Portfolio at a glance — charts from the X-ray (deterministic) */}
          {xray && ((xray.allocation?.by_sector?.length ?? 0) > 0 || (xray.concentration?.top_holdings?.length ?? 0) > 0) && (
            <div className="hrv-kept" style={{ marginTop: 28, border: "1px solid var(--a-border-subtle)", borderRadius: 16, padding: "18px 20px" }}>
              <div style={{ ...MONO, fontSize: 9.5, textTransform: "uppercase", letterSpacing: "0.12em", color: "var(--a-text-muted)", marginBottom: 16 }}>Allocation &amp; concentration</div>
              <div style={{ display: "flex", gap: 28, flexWrap: "wrap" }}>
                {(xray.allocation?.by_sector?.length ?? 0) > 0 && (
                  <div style={{ flex: "1 1 280px", minWidth: isMobile ? 0 : 240 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--a-text-body)", marginBottom: 10 }}>By sector</div>
                    {xray.allocation!.by_sector.slice(0, 6).map((s) => {
                      const over = s.pct > 30;
                      return (
                        <div key={s.name} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 7 }}>
                          <span style={{ width: 96, fontSize: 11.5, color: "var(--a-text-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.name}</span>
                          <div style={{ flex: 1, height: 10, background: "var(--ink-100)", borderRadius: 5 }}>
                            <div style={{ width: `${Math.min(100, s.pct)}%`, height: "100%", borderRadius: 5, background: over ? "var(--lumen-500)" : "var(--veris-500)" }} />
                          </div>
                          <span style={{ ...MONO, width: 34, textAlign: "right", fontSize: 11, color: over ? WARN : "var(--a-text-strong)" }}>{s.pct}%</span>
                        </div>
                      );
                    })}
                  </div>
                )}
                {(xray.concentration?.top_holdings?.length ?? 0) > 0 && (
                  <div style={{ flex: "1 1 280px", minWidth: isMobile ? 0 : 240 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--a-text-body)", marginBottom: 10 }}>
                      Top holdings <span style={{ color: "var(--a-text-subtle)", fontWeight: 400 }}>· top-5 {xray.concentration?.top5_pct ?? "—"}%</span>
                    </div>
                    {xray.concentration!.top_holdings.slice(0, 5).map((h) => {
                      const over = h.pct > 15;
                      return (
                        <div key={h.ticker} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 7 }}>
                          <span style={{ ...MONO, width: 84, fontSize: 11, color: "var(--a-text-body)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{h.ticker}</span>
                          <div style={{ flex: 1, height: 10, background: "var(--ink-100)", borderRadius: 5 }}>
                            <div style={{ width: `${Math.min(100, h.pct * 4)}%`, height: "100%", borderRadius: 5, background: over ? "var(--lumen-500)" : "var(--veris-500)" }} />
                          </div>
                          <span style={{ ...MONO, width: 34, textAlign: "right", fontSize: 11, color: over ? WARN : "var(--a-text-strong)" }}>{h.pct}%</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              {(xray.allocation?.by_cap_tier?.length ?? 0) > 0 && (
                <div style={{ marginTop: 18 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--a-text-body)", marginBottom: 8 }}>By market cap</div>
                  <div style={{ display: "flex", height: 12, borderRadius: 6, overflow: "hidden", background: "var(--ink-100)" }}>
                    {xray.allocation!.by_cap_tier.map((c, i) => (
                      <div key={c.name} title={`${c.name} ${c.pct}%`} style={{ width: `${c.pct}%`, background: ["var(--veris-600)", "var(--veris-400)", "var(--veris-300)", "var(--ink-300)"][i] || "var(--ink-200)" }} />
                    ))}
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 14px", marginTop: 8 }}>
                    {xray.allocation!.by_cap_tier.map((c, i) => (
                      <span key={c.name} style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, color: "var(--a-text-muted)" }}>
                        <span style={{ width: 9, height: 9, borderRadius: 2, background: ["var(--veris-600)", "var(--veris-400)", "var(--veris-300)", "var(--ink-300)"][i] || "var(--ink-200)" }} />{c.name} <span style={MONO}>{c.pct}%</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <p style={{ fontSize: 11.5, color: "var(--a-text-subtle)", margin: "14px 0 0" }}>Amber marks a position &gt;15% or a sector &gt;30% of the equity sleeve. Descriptive — not advice.</p>
            </div>
          )}

          {/* Prose sections */}
          {report.sections.map((s, i) => {
            const isRisk = /risk|drawdown/i.test(s.title);
            return (
              <section key={i} id={secId(i)} style={{ marginTop: 38, scrollMarginTop: 72 }}>
                <h2 style={{ fontFamily: DISPLAY, fontSize: 21, fontWeight: 600, letterSpacing: "-0.022em", margin: "0 0 10px", color: "var(--a-text-strong)" }}>{s.title}</h2>
                <p style={{ fontSize: 15, lineHeight: 1.7, color: "var(--a-text-body)", margin: 0 }}>{s.body}</p>
                {isRisk && stress.length > 0 && (
                  <div className="hrv-kept" style={{ marginTop: 16, background: "#FEF1F1", border: "1px solid rgba(199,58,63,0.28)", borderRadius: 14, padding: "16px 18px" }}>
                    <div style={{ ...MONO, fontSize: 9.5, textTransform: "uppercase", letterSpacing: "0.1em", color: BAD, marginBottom: 10 }}>Stress test · beta-adjusted (β {(xray?.risk?.beta_vs_nifty50 ?? 1).toFixed(2)})</div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 14 }}>
                      {stress.map((st) => (
                        <div key={st.p}>
                          <div style={{ ...MONO, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--a-text-muted)" }}>If Nifty 50 falls {st.p}%</div>
                          <div style={{ fontFamily: DISPLAY, fontSize: 19, fontWeight: 600, color: BAD, marginTop: 2 }}>−{lakh(st.loss)}</div>
                        </div>
                      ))}
                    </div>
                    <p style={{ fontSize: 12, color: "var(--a-text-subtle)", margin: "10px 0 0" }}>A linear beta estimate. Real drawdowns are non-linear — treat these as orientation, not forecasts.</p>
                  </div>
                )}
              </section>
            );
          })}

          {/* Disclaimer */}
          <div style={{ marginTop: 32, paddingTop: 16, borderTop: "1px solid var(--a-border-subtle)" }}>
            <p style={{ fontSize: 12.5, lineHeight: 1.55, color: "var(--a-text-subtle)", margin: 0 }}>
              {report.disclaimer} Figures are computed from your holdings against end-of-day NSE/BSE closes and may lag or contain errors. Not a SEBI-registered research or advisory service; nothing here is a buy/sell recommendation.
            </p>
          </div>

          {/* Close bar */}
          <div className="hrv-chrome" style={{ marginTop: 20, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", border: "1px solid var(--a-border-subtle)", borderRadius: 14, padding: "14px 18px", background: "var(--surface-sunken, var(--ink-50))" }}>
            <span style={{ fontSize: 13.5, color: "var(--a-text-body)" }}>Keep a copy, or ask about anything in it.</span>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="a-btn a-btn-secondary" style={{ padding: "8px 14px", fontSize: 13 }} onClick={() => window.print()}>Download PDF</button>
              <button className="a-btn a-btn-primary" style={{ padding: "8px 14px", fontSize: 13 }} onClick={() => { onClose(); onAsk?.(); }}>Ask about this</button>
            </div>
          </div>
        </div>
      </div>

      <style>{`@media print {
        body * { visibility: hidden !important; }
        #hrv-overlay, #hrv-overlay * { visibility: visible !important; }
        #hrv-overlay { position: absolute !important; inset: 0 !important; background: #fff !important; padding: 0 !important; overflow: visible !important; }
        #hrv-overlay .hrv-doc { box-shadow: none !important; border-radius: 0 !important; max-width: 100% !important; }
        #hrv-overlay .hrv-chrome { display: none !important; }
        .hrv-kept, table, section { break-inside: avoid; }
        thead { display: table-header-group; }
      }`}</style>
    </div>
  );
}
